/**
 * Platform-wide constants. Values here are business defaults, never secrets.
 */
/** Default currency for all merchants (Malaysian Ringgit). */
export const DEFAULT_CURRENCY = 'MYR';
/** Currencies the platform accepts in Phase 1. */
export const SUPPORTED_CURRENCIES = ['MYR'];
/** Default merchant timezone. All timestamps are stored in UTC internally. */
export const DEFAULT_TIMEZONE = 'Asia/Kuala_Lumpur';
/** Default country for merchant/customer addresses (ISO 3166-1 alpha-2). */
export const DEFAULT_COUNTRY = 'MY';
/** Number of decimal places used for MYR money values. */
export const MONEY_SCALE = 2;
/**
 * Malaysian states and federal territories, used for shipping zones and
 * address forms.
 */
export const MALAYSIAN_STATES = [
    { code: 'JHR', name: 'Johor' },
    { code: 'KDH', name: 'Kedah' },
    { code: 'KTN', name: 'Kelantan' },
    { code: 'MLK', name: 'Melaka' },
    { code: 'NSN', name: 'Negeri Sembilan' },
    { code: 'PHG', name: 'Pahang' },
    { code: 'PNG', name: 'Pulau Pinang' },
    { code: 'PRK', name: 'Perak' },
    { code: 'PLS', name: 'Perlis' },
    { code: 'SBH', name: 'Sabah' },
    { code: 'SWK', name: 'Sarawak' },
    { code: 'SGR', name: 'Selangor' },
    { code: 'TRG', name: 'Terengganu' },
    { code: 'KUL', name: 'Wilayah Persekutuan Kuala Lumpur' },
    { code: 'LBN', name: 'Wilayah Persekutuan Labuan' },
    { code: 'PJY', name: 'Wilayah Persekutuan Putrajaya' },
];
export const MALAYSIAN_STATE_CODES = MALAYSIAN_STATES.map((s) => s.code);
/**
 * Regions used to group states into shipping zones. West Malaysia
 * (Semenanjung) and East Malaysia (Sabah/Sarawak/Labuan) usually carry
 * different postage rates.
 */
export const EAST_MALAYSIA_STATES = ['SBH', 'SWK', 'LBN'];
export function isEastMalaysia(stateCode) {
    return EAST_MALAYSIA_STATES.includes(stateCode.toUpperCase());
}
/** Slugs a merchant may not claim, because they collide with platform hosts. */
export const RESERVED_MERCHANT_SLUGS = [
    'www',
    'api',
    'app',
    'admin',
    'dashboard',
    'static',
    'assets',
    'cdn',
    'mail',
    'smtp',
    'status',
    'support',
    'help',
    'docs',
    'blog',
    'store',
    'shop',
    'checkout',
    'pay',
    'payment',
    'billing',
    'account',
    'login',
    'register',
    'musleempay',
];
/** Pagination defaults applied by every list endpoint. */
export const PAGINATION = {
    DEFAULT_PAGE: 1,
    DEFAULT_PAGE_SIZE: 20,
    MAX_PAGE_SIZE: 100,
};
/** Cookie names for the access/refresh token pair and the CSRF token. */
export const COOKIE_NAMES = {
    ACCESS_TOKEN: 'mp_at',
    REFRESH_TOKEN: 'mp_rt',
    CSRF_TOKEN: 'mp_csrf',
};
/** Header the browser must echo the CSRF cookie in (double-submit pattern). */
export const CSRF_HEADER = 'x-mp-csrf';
/**
 * Carries the id of a SupportAccessGrant: a platform operator's time-limited,
 * VIEW-ONLY access to one merchant's account. Honoured only for platform
 * admins, only on safe (read) methods, and only while the grant is live.
 */
export const SUPPORT_ACCESS_HEADER = 'x-support-access';
/** How long a support grant lasts. Re-requesting is deliberate, not a nuisance. */
export const SUPPORT_ACCESS_TTL_MINUTES = 30;
/** Header carrying a client-supplied idempotency key on checkout. */
export const IDEMPOTENCY_HEADER = 'x-idempotency-key';
/** BullMQ queue names. Keep in sync with apps/worker processors. */
export const QUEUE_NAMES = {
    EMAIL: 'email',
    INVOICE: 'invoice',
};
/** Named BullMQ jobs within each queue. */
export const JOB_NAMES = {
    SEND_EMAIL: 'send-email',
    GENERATE_INVOICE: 'generate-invoice',
};
/** Onboarding wizard steps, in order. Persisted on Merchant.onboardingStep. */
export const ONBOARDING_STEPS = [
    'ACCOUNT',
    'VERIFY_EMAIL',
    'BUSINESS_INFO',
    'STORE_DETAILS',
    'PAYMENT_SETUP',
    'SHIPPING_SETUP',
    'FIRST_PRODUCT',
    'FIRST_SALES_PAGE',
    'COMPLETED',
];
export function nextOnboardingStep(current) {
    const index = ONBOARDING_STEPS.indexOf(current);
    const next = ONBOARDING_STEPS[index + 1];
    return next ?? 'COMPLETED';
}
export function onboardingProgressPercent(current) {
    const index = ONBOARDING_STEPS.indexOf(current);
    const lastIndex = ONBOARDING_STEPS.length - 1;
    if (index <= 0)
        return 0;
    return Math.round((index / lastIndex) * 100);
}
export * from './activity.js';
export * from './plans.js';
export * from './gateways.js';
//# sourceMappingURL=index.js.map